import { Component, Input, forwardRef, OnInit } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR, FormControl } from '@angular/forms';

interface CountryCode {
  code: string;
  flag: string;
  name: string;
  dialCode: string;
  maxLength: number;
}

@Component({
  selector: 'app-phone-input',
  template: `
    <div class="input-group">
      <div class="input-group-prepend">
        <button 
          type="button" 
          class="btn btn-outline-secondary dropdown-toggle" 
          data-bs-toggle="dropdown" 
          aria-expanded="false"
          style="border-top-right-radius: 0; border-bottom-right-radius: 0;">
          <span class="me-1">{{selectedCountry.flag}}</span>
          <span class="me-1">{{selectedCountry.dialCode}}</span>
        </button>
        <ul class="dropdown-menu">
          <li *ngFor="let country of countries">
            <a class="dropdown-item" href="#" (click)="selectCountry(country, $event)">
              <span class="me-2">{{country.flag}}</span>
              <span class="me-2">{{country.name}}</span>
              <span class="text-muted">{{country.dialCode}}</span>
            </a>
          </li>
        </ul>
      </div>
      <input 
        type="tel" 
        class="form-control" 
        [placeholder]="placeholder"
        [value]="phoneControl.value"
        [attr.maxlength]="selectedCountry.maxLength"
        (input)="onPhoneChange($event)"
        (blur)="onBlur()"
        [disabled]="disabled"
        inputmode="tel"
        style="border-top-left-radius: 0; border-bottom-left-radius: 0;" />
    </div>
    <small class="text-muted mt-1 d-block" *ngIf="showFormat">
      Formato: {{getFormatExample()}}
    </small>
  `,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => PhoneInputComponent),
      multi: true
    }
  ],
  styleUrls: []
})
export class PhoneInputComponent implements ControlValueAccessor, OnInit {
  @Input() placeholder: string = 'Número de teléfono';
  @Input() showFormat: boolean = true;
  @Input() disabled: boolean = false;

  phoneControl = new FormControl('');
  
  selectedCountry: CountryCode = {
    code: 'MX',
    flag: '🇲🇽',
    name: 'México',
    dialCode: '+52',
    maxLength: 10
  };

  countries: CountryCode[] = [
    { code: 'MX', flag: '🇲🇽', name: 'México', dialCode: '+52', maxLength: 10 },
    { code: 'US', flag: '🇺🇸', name: 'Estados Unidos', dialCode: '+1', maxLength: 10 },
    { code: 'CA', flag: '🇨🇦', name: 'Canadá', dialCode: '+1', maxLength: 10 },
    { code: 'GT', flag: '🇬🇹', name: 'Guatemala', dialCode: '+502', maxLength: 8 },
    { code: 'ES', flag: '🇪🇸', name: 'España', dialCode: '+34', maxLength: 9 },
    { code: 'CO', flag: '🇨🇴', name: 'Colombia', dialCode: '+57', maxLength: 10 },
    { code: 'AR', flag: '🇦🇷', name: 'Argentina', dialCode: '+54', maxLength: 10 },
    { code: 'CL', flag: '🇨🇱', name: 'Chile', dialCode: '+56', maxLength: 9 },
    { code: 'PE', flag: '🇵🇪', name: 'Perú', dialCode: '+51', maxLength: 9 },
  ];

  private onChange = (value: string) => {};
  private onTouched = () => {};

  ngOnInit() {
    // If a phone number is already set, parse it to extract country code
    this.parseExistingNumber();
  }

  onBlur() {
    this.onTouched();
  }

  selectCountry(country: CountryCode, event: Event) {
    event.preventDefault();
    this.selectedCountry = country;
    this.emitValue();
  }

  onPhoneChange(event: any) {
    const input = event.target.value;
    // Remove any non-numeric characters
    const cleanNumber = input.replaceAll(/\D/g, '');
    
    // Update the form control with clean number
    this.phoneControl.setValue(cleanNumber, { emitEvent: false });
    
    this.emitValue();
  }

  private emitValue() {
    const phoneNumber = this.phoneControl.value || '';
    const fullNumber = phoneNumber ? `${this.selectedCountry.dialCode}${phoneNumber}` : '';
    this.onChange(fullNumber);
  }

  private parseExistingNumber() {
    const currentValue = this.phoneControl.value;
    if (currentValue) {
      // Try to match against known country codes
      for (const country of this.countries) {
        if (currentValue.startsWith(country.dialCode)) {
          this.selectedCountry = country;
          const phoneNumber = currentValue.substring(country.dialCode.length);
          this.phoneControl.setValue(phoneNumber, { emitEvent: false });
          break;
        }
      }
    }
  }

  getFormatExample(): string {
    switch (this.selectedCountry.code) {
      case 'MX':
        return '55 1234 5678';
      case 'US':
      case 'CA':
        return '(555) 123-4567';
      case 'ES':
        return '612 34 56 78';
      default:
        return 'Ejemplo: ' + '1'.repeat(this.selectedCountry.maxLength);
    }
  }

  // ControlValueAccessor implementation
  writeValue(value: string): void {
    if (value) {
      // Parse the incoming value to separate country code and number
      for (const country of this.countries) {
        if (value.startsWith(country.dialCode)) {
          this.selectedCountry = country;
          const phoneNumber = value.substring(country.dialCode.length);
          this.phoneControl.setValue(phoneNumber, { emitEvent: false });
          return;
        }
      }
      // If no country code found, just set the raw value
      this.phoneControl.setValue(value, { emitEvent: false });
    } else {
      this.phoneControl.setValue('', { emitEvent: false });
    }
  }

  registerOnChange(fn: (value: string) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
    if (isDisabled) {
      this.phoneControl.disable();
    } else {
      this.phoneControl.enable();
    }
  }
}